import type { ComponentProps } from 'react';
import { Link as RouterLink, type To } from 'react-router-dom';
import { Icon, Link, Text } from '@/ui';

type Props = {
  label: string;
  isActive?: boolean;
  to?: To;
  onClick?: () => void;
  icon?: ComponentProps<typeof Icon>['icon'];
  /** Hide the text label below `md`, leaving only the icon (kept accessible via aria-label). */
  hideLabelOnMobile?: boolean;
  // Interactive crumbs never shrink by default; set a max width to let a
  // long label truncate instead of crowding its siblings.
  maxW?: ComponentProps<typeof Link>['maxW'];
};

const interactiveStyles = {
  alignItems: 'center',
  borderRadius: 'md',
  color: 'ink.muted',
  display: 'inline-flex',
  flexShrink: 0,
  fontSize: 'ah-control',
  fontWeight: 400,
  lineHeight: 1,
  gap: '1.5',
  px: 2,
  py: 0.5,
  _hover: {
    bg: 'rgba(0,0,0,0.04)',
    color: 'ink.soft',
    textDecoration: 'none',
  },
} as const;

export const Breadcrumb = ({
  label,
  isActive,
  to,
  onClick,
  icon,
  hideLabelOnMobile,
  maxW,
}: Props) => {
  const labelNode =
    hideLabelOnMobile || maxW ? (
      <Text
        as="span"
        display={hideLabelOnMobile ? { base: 'none', md: 'inline' } : undefined}
        maxW={maxW}
        noOfLines={maxW ? 1 : undefined}
      >
        {label}
      </Text>
    ) : (
      label
    );

  const content = (
    <>
      {icon && <Icon color="ink.muted" icon={icon} size={3.5} />}
      {labelNode}
    </>
  );

  // When the label is visually hidden on mobile, keep the accessible name on
  // every branch (display:none removes the text from the a11y tree entirely).
  const ariaLabel = hideLabelOnMobile ? label : undefined;

  // The crumb itself must be allowed to shrink for the inner label to
  // truncate.
  const truncationStyles = maxW
    ? ({ flexShrink: 1, minW: 0 } as const)
    : undefined;

  if (isActive) {
    return (
      <Text
        aria-current="page"
        aria-label={ariaLabel}
        color="ink.strong"
        fontSize="ah-control"
        fontWeight={600}
        lineHeight={1}
        minW={0}
        overflow="hidden"
        px={2}
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {content}
      </Text>
    );
  }

  if (to) {
    return (
      <Link
        aria-label={ariaLabel}
        as={RouterLink}
        to={to}
        {...interactiveStyles}
        {...truncationStyles}
        onClick={onClick}
      >
        {content}
      </Link>
    );
  }

  if (onClick) {
    return (
      <Link
        aria-label={ariaLabel}
        as="button"
        type="button"
        {...interactiveStyles}
        {...truncationStyles}
        onClick={onClick}
      >
        {content}
      </Link>
    );
  }

  return (
    <Text
      aria-label={ariaLabel}
      color="ink.muted"
      flexShrink={0}
      fontSize="ah-control"
      fontWeight={400}
      lineHeight={1}
    >
      {content}
    </Text>
  );
};
